import React, { useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import Banner from '../Components/Banner';
import { ChevronRight } from 'lucide-react';
import { Helmet } from "react-helmet-async";
import ServiceNowLeadForm from '../Components/ServiceNowLeadForm';

function HireDatabricksConsultantFrance() {

  const databricksFaqData = [
    {
      id: 1,
      question: "How can I hire a Databricks consultant in France?",
      answer:
        "You can hire a Databricks consultant in France based on your data engineering goals, platform requirements, and project scope. Mobikode can help you plan and deliver Databricks solutions for data processing, analytics, lakehouse architecture, and pipeline development."
    },
    {
      id: 2,
      question: "What skills do Databricks consultants bring?",
      answer:
        "Databricks consultants can work with Apache Spark, Python, SQL, Delta Lake, data pipelines, lakehouse architecture, workflow orchestration, performance optimization, and cloud data platforms."
    },
    {
      id: 3,
      question: "Which Databricks services can I hire a consultant for?",
      answer:
        "You can hire consultants for Databricks implementation, data engineering, ETL and ELT pipelines, Delta Lake, Lakehouse architecture, analytics, platform migration, optimization, and integrations."
    },
    {
      id: 4,
      question: "Can Databricks consultants work with our existing data team?",
      answer:
        "Yes. Consultants can collaborate with your internal data engineers, analysts, architects, and cloud teams while following your existing workflows, governance requirements, and technical standards."
    },
    {
      id: 5,
      question: "Can I hire a Databricks consultant for a specific project?",
      answer:
        "Yes. You can engage a Databricks consultant for a defined project such as building data pipelines, migrating workloads, implementing a lakehouse, improving performance, or supporting ongoing platform development."
    },
    {
      id: 6,
      question: "Why hire Databricks consultants from Mobikode?",
      answer:
        "Mobikode can provide technical resources to support Databricks implementation, data engineering, integrations, pipeline development, platform optimization, and ongoing data initiatives."
    }
  ];

  const [activeIndex, setActiveIndex] = useState(0);
  const [faqActiveIndex, setFaqActiveIndex] = useState(0);

  const toggleFaqAccordion = (index) => {
    setFaqActiveIndex(faqActiveIndex === index ? -1 : index);
  };

  return (
    <div>

      <Helmet>

        {/* Primary SEO */}
        <title>
          Hire Databricks Consultant in France | Mobikode
        </title>

        <meta
          name="description"
          content="Hire Databricks consultants in France for data engineering, Apache Spark development, Delta Lake, lakehouse architecture, data pipelines, integrations, and analytics optimization."
        />

        <meta
          name="keywords"
          content="hire Databricks consultant France, Databricks consulting France, Databricks consultants France TX, Databricks data engineering services, Databricks implementation France, Apache Spark consultants, Delta Lake consulting, Databricks lakehouse architecture, Databricks pipeline development, Databricks optimization services"
        />

        <link
          rel="canonical"
          href="https://www.mobikode.com/hire-databricks-consultant-France/"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Hire Databricks Consultant in France | Mobikode"
        />

        <meta
          property="og:description"
          content="Hire Databricks consultants in France for data engineering, lakehouse architecture, Apache Spark development, data pipelines, analytics, integrations, and platform optimization."
        />

        <meta
          property="og:url"
          content="https://www.mobikode.com/hire-databricks-consultant-France/"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:site_name"
          content="Mobikode"
        />

        <meta
          property="og:image"
          content="https://www.mobikode.com/assets/services/hire-servicenow-developers.jpg"
        />

        <meta
          property="og:image:alt"
          content="Hire Databricks Consultants in France"
        />

        {/* Twitter / X */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Hire Databricks Consultant in France | Mobikode"
        />

        <meta
          name="twitter:description"
          content="Hire Databricks consultants in France for data engineering, lakehouse architecture, pipeline development, analytics, integrations, and platform optimization."
        />

        <meta
          name="twitter:image"
          content="https://www.mobikode.com/assets/services/hire-servicenow-developers.jpg"
        />

        <meta
          name="twitter:image:alt"
          content="Hire Databricks Consultants in France"
        />

      </Helmet>


      {/* =========================================================
          HERO SECTION
      ========================================================= */}

      <section className="relative h-auto lg:h-[630px] w-full overflow-hidden bg-[#0a0b1e]">

        <div className="absolute inset-0 z-0">

          <img
            src="/assets/services/bg3.png"
            alt="Hire Databricks consultants in France TX"
            className="h-full w-full object-cover opacity-40"
          />

          <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#0a0b1e] via-[#0a0b1e]/80 to-transparent opacity-90" />

        </div>


        <div className="relative z-10 flex h-full max-w-[1440px] w-full mx-auto flex-col justify-between px-6 lg:px-20 pt-20 pb-[60px]">

          {/* Hero Heading */}

          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
            className="lg:mt-[115px] mt-[40px]"
          >

            <h1 className="text-[38px] lg:text-[55px] text-[#FFFFFF] max-w-[800px] w-full font-sora font-[600] leading-[1.2] mb-[22px]">

              Hire Databricks Consultants in France

            </h1>

            <p className="text-[#FFFFFF] font-graphik text-[16px] md:text-[20px] font-[400] leading-[1.6] mb-4">

              Experienced Databricks Consulting for Your Data Projects in France

            </p>

          </motion.div>


          {/* Hero Description */}

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 lg:self-end max-w-full lg:max-w-[501px] lg:ml-auto text-left md:mt-0"
          >

            <p className="text-[#FFFFFF] font-graphik text-[14px] md:text-[16px] font-[400] leading-[1.6]">

             Hire Databricks consultants in France to build reliable data pipelines, optimize analytics workloads, and scale your lakehouse architecture.

            </p>

          </motion.div>

        </div>

      </section>


      {/* =========================================================
          SERVICE OVERVIEW
      ========================================================= */}

      <section className="bg-[#FFFFFF] overflow-hidden lg:py-[100px] py-[60px]">

        <div className="max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px]">

          <div className="flex flex-col lg:flex-row items-center gap-[40px] lg:gap-[80px]">

            {/* LEFT IMAGE */}

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="w-full lg:w-1/2"
            >

              <div className="w-full h-[280px] md:h-[380px] lg:h-[450px] overflow-hidden">

                <img
                  src="/assets/sales/img2.png"
                  alt="Databricks development services in France TX"
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />

              </div>

            </motion.div>


            {/* RIGHT CONTENT */}

            <div className="w-full lg:w-1/2 text-left">

              {/* Service Overview Label */}

              <motion.div
                initial={{ opacity: 0, y: -15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex items-center gap-[10px] pb-[15px] lg:pb-[20px]"
              >

                <motion.span
                  className="font-[500] font-graphik text-[16px] lg:text-[20px] leading-[1.6] text-[#01386A]"
                >

                  Service Overview

                </motion.span>

              </motion.div>


              {/* Title */}

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="font-[600] font-sora lg:text-[30px] max-w-[850px] w-full text-[20px] leading-[1.3] text-[#01386A]"
              >

                Hire Experienced Databricks Consultants in France

              </motion.h2>


              {/* Description */}

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.35 }}
                className="font-[400] max-w-[800px] w-full font-graphik text-[14px] lg:text-[16px] leading-[1.6] lg:leading-[22px] text-[#01386A] mt-[20px] lg:mt-[25px]"
              >

                Hire Databricks consultants in France with the technical expertise to design, build, integrate, and optimize modern data solutions. Mobikode can support Databricks implementation, Apache Spark development, Delta Lake, data engineering, ETL and ELT pipelines, analytics, and lakehouse architecture.

                <br /><br />

                Whether you need specialist support for a defined initiative or consultants for ongoing data platform work, our flexible engagement model helps you add technical capacity to your team. Consultants can collaborate with your existing data engineers, analysts, architects, and cloud teams while aligning with your project requirements and data roadmap.

              </motion.p>


              {/* Bottom Accent */}

              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: "70px" }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="h-[3px] bg-[#ED6800] mt-[25px] lg:mt-[35px]"
              />

            </div>

          </div>

        </div>

      </section>


      {/* =========================================================
          SERVICE NOW EXPERTS
      ========================================================= */}

      <section className="w-full bg-[#FDFDFD] py-16 lg:py-24 relative">

        <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">

          <div className="flex flex-col lg:flex-row gap-10 lg:gap-20 items-start">

            {/* Left Side */}

            <div className="w-full lg:w-[40%] lg:sticky lg:top-24">

              <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] text-left">

                Databricks Consultants and Technical Experts in France

              </h2>

            </div>


            {/* Right Side */}

            <div className="w-full lg:w-[60%] flex flex-col gap-6">


              {/* Card 1 */}

              <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">

                <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">

                  Databricks Consultants

                </h3>

                <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">

                  Hire Databricks consultants in France experienced in scripting,
                  lakehouse architecture, Apache Spark workloads, Delta Lake, data pipelines,
                  and data platform optimization. Our consultants can support
                  implementation, enhancements, and ongoing data engineering.

                </p>

              </div>


              {/* Card 2 */}

              <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">

                <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">

                  Databricks Consultants

                </h3>

                <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">

                  Add Databricks consultants to support data strategy, solution planning,
                  architecture decisions, governance, and technical coordination
                  alongside your data engineering team.

                </p>

              </div>


              {/* Card 3 */}

              <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">

                <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">

                  Databricks Architects

                </h3>

                <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">

                  Hire data architects with Databricks expertise to plan scalable lakehouse
                  architecture, data models, governance, cloud integrations, and
                  enterprise analytics solutions.

                </p>

              </div>


              {/* Card 4 */}

              <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">

                <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">

                  Databricks Administrators

                </h3>

                <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">

                  Extend your Databricks team with specialists who can support workspace
                  configuration, access controls, cluster policies, job scheduling,
                  monitoring, and day-to-day platform operations.

                </p>

              </div>


              {/* Card 5 */}

              <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">

                <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">

                  Databricks Integration Specialists

                </h3>

                <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">

                  Add integration specialists to connect Databricks with cloud storage,
                  databases, business applications, and analytics tools while supporting
                  reliable ingestion, transformation, and data synchronization.

                </p>

              </div>


              {/* Card 6 */}

              <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">

                <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">

                  Databricks QA & Testing Professionals

                </h3>

                <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">

                  Strengthen data quality with specialists who can validate pipelines,
                  test transformations, monitor data reliability, and support
                  quality checks across Databricks workloads.

                </p>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =========================================================
          SERVICENOW DEVELOPMENT EXPERTISE
      ========================================================= */}

      <section className="w-full bg-white py-16 lg:py-24">

        <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">

          <h2 className="text-[#01386A] text-[20px] md:text-[30px] font-[600] font-sora mb-6 lg:mb-10 text-left">

            Databricks Consulting Expertise in France

          </h2>


          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">

            {/* LEFT SIDE - TABS */}

            <div className="lg:w-1/2 flex flex-col">


              {/* Data Engineering */}

              <div
                onClick={() => setActiveIndex(0)}
                className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                  activeIndex === 0
                    ? "border-[#ED6800]"
                    : "border-gray-300 hover:border-gray-600"
                }`}
              >

                <h3
                  className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                    activeIndex === 0
                      ? "text-[#01386A]"
                      : "text-[#0000008C] group-hover:text-gray-600"
                  }`}
                >

                  Databricks Data Engineering

                </h3>

                <ChevronRight
                  size={24}
                  className={`transition-all duration-300 ${
                    activeIndex === 0
                      ? "opacity-100 translate-x-0 text-[#ED6800]"
                      : "opacity-0 -translate-x-4 text-gray-300"
                  }`}
                />

              </div>


              {/* Lakehouse Architecture */}

              <div
                onClick={() => setActiveIndex(1)}
                className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                  activeIndex === 1
                    ? "border-[#ED6800]"
                    : "border-gray-300 hover:border-gray-600"
                }`}
              >

                <h3
                  className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                    activeIndex === 1
                      ? "text-[#01386A]"
                      : "text-[#0000008C] group-hover:text-gray-600"
                  }`}
                >

                  Lakehouse Architecture & Delta Lake

                </h3>

                <ChevronRight
                  size={24}
                  className={`transition-all duration-300 ${
                    activeIndex === 1
                      ? "opacity-100 translate-x-0 text-[#ED6800]"
                      : "opacity-0 -translate-x-4 text-gray-300"
                  }`}
                />

              </div>


              {/* Spark Optimization */}

              <div
                onClick={() => setActiveIndex(2)}
                className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                  activeIndex === 2
                    ? "border-[#ED6800]"
                    : "border-gray-300 hover:border-gray-600"
                }`}
              >

                <h3
                  className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                    activeIndex === 2
                      ? "text-[#01386A]"
                      : "text-[#0000008C] group-hover:text-gray-600"
                  }`}
                >

                  Apache Spark & Performance Optimization

                </h3>

                <ChevronRight
                  size={24}
                  className={`transition-all duration-300 ${
                    activeIndex === 2
                      ? "opacity-100 translate-x-0 text-[#ED6800]"
                      : "opacity-0 -translate-x-4 text-gray-300"
                  }`}
                />

              </div>


              {/* ETL & ELT Pipelines */}

              <div
                onClick={() => setActiveIndex(3)}
                className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                  activeIndex === 3
                    ? "border-[#ED6800]"
                    : "border-gray-300 hover:border-gray-600"
                }`}
              >

                <h3
                  className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                    activeIndex === 3
                      ? "text-[#01386A]"
                      : "text-[#0000008C] group-hover:text-gray-600"
                  }`}
                >

                  ETL & ELT Pipeline Development

                </h3>

                <ChevronRight
                  size={24}
                  className={`transition-all duration-300 ${
                    activeIndex === 3
                      ? "opacity-100 translate-x-0 text-[#ED6800]"
                      : "opacity-0 -translate-x-4 text-gray-300"
                  }`}
                />

              </div>


              {/* Integrations */}

              <div
                onClick={() => setActiveIndex(4)}
                className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                  activeIndex === 4
                    ? "border-[#ED6800]"
                    : "border-gray-300 hover:border-gray-600"
                }`}
              >

                <h3
                  className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                    activeIndex === 4
                      ? "text-[#01386A]"
                      : "text-[#0000008C] group-hover:text-gray-600"
                  }`}
                >

                  Data Platform Integrations

                </h3>

                <ChevronRight
                  size={24}
                  className={`transition-all duration-300 ${
                    activeIndex === 4
                      ? "opacity-100 translate-x-0 text-[#ED6800]"
                      : "opacity-0 -translate-x-4 text-gray-300"
                  }`}
                />

              </div>


              {/* Custom Development */}

              <div
                onClick={() => setActiveIndex(5)}
                className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                  activeIndex === 5
                    ? "border-[#ED6800]"
                    : "border-gray-300 hover:border-gray-600"
                }`}
              >

                <h3
                  className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                    activeIndex === 5
                      ? "text-[#01386A]"
                      : "text-[#0000008C] group-hover:text-gray-600"
                  }`}
                >

                  Databricks Analytics & BI

                </h3>

                <ChevronRight
                  size={24}
                  className={`transition-all duration-300 ${
                    activeIndex === 5
                      ? "opacity-100 translate-x-0 text-[#ED6800]"
                      : "opacity-0 -translate-x-4 text-gray-300"
                  }`}
                />

              </div>

            </div>


            {/* RIGHT SIDE */}

            <div className="lg:w-1/2 flex flex-col justify-start">

              <div className="min-h-[100px] mb-8">

                {activeIndex === 0 && (
                  <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">

                    Hire Databricks consultants in France to build scalable data engineering
                    workflows, develop ETL and ELT pipelines, transform large datasets,
                    and prepare reliable data for reporting and analytics.

                  </p>
                )}


                {activeIndex === 1 && (
                  <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">

                    Add Databricks consultants to design lakehouse architectures using Delta Lake,
                    organize data across layers, and improve reliability, governance,
                    and accessibility for analytics workloads.

                  </p>
                )}


                {activeIndex === 2 && (
                  <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">

                    Hire Databricks consultants to tune Apache Spark workloads, optimize queries,
                    improve cluster utilization, and troubleshoot performance issues
                    across large-scale data processing jobs.

                  </p>
                )}


                {activeIndex === 3 && (
                  <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">

                    Build dependable ETL and ELT pipelines with Databricks consultants who can
                    support ingestion, transformation, orchestration, validation,
                    and ongoing pipeline improvements.

                  </p>
                )}


                {activeIndex === 4 && (
                  <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">

                    Hire Databricks integration consultants to connect data sources, cloud storage,
                    warehouses, and business systems. Support batch and streaming
                    ingestion, data synchronization, and dependable data workflows.

                  </p>
                )}


                {activeIndex === 5 && (
                  <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">

                    Use Databricks for analytics-ready datasets and reporting workflows. Consultants
                    can help prepare trusted data, support BI integrations, and improve
                    the performance and maintainability of your analytics platform.

                  </p>
                )}

              </div>


              {/* IMAGE */}

              <div className="w-full h-[300px] lg:h-[378px] overflow-hidden relative">

                <div className="absolute inset-0 bg-gradient-to-tr from-blue-100/50 to-transparent z-10 pointer-events-none"></div>


                {activeIndex === 0 && (
                  <img
                    src="/assets/home-/ai-2.png"
                    alt="Databricks data engineering in France"
                    className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                  />
                )}


                {activeIndex === 1 && (
                  <img
                    src="/assets/home-/ai-3.png"
                    alt="Databricks lakehouse architecture in France"
                    className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                  />
                )}


                {activeIndex === 2 && (
                  <img
                    src="/assets/home-/ai-4.png"
                    alt="Apache Spark optimization in France"
                    className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                  />
                )}


                {activeIndex === 3 && (
                  <img
                    src="/assets/home-/img1.png"
                    alt="ETL and ELT pipeline development in France"
                    className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                  />
                )}


                {activeIndex === 4 && (
                  <img
                    src="/assets/home-/img2.png"
                    alt="Databricks integrations in France"
                    className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                  />
                )}


                {activeIndex === 5 && (
                  <img
                    src="/assets/home-/img3.png"
                    alt="Custom Databricks development in France"
                    className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                  />
                )}

              </div>

            </div>

          </div>

        </div>


        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }

            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-fade-in {
            animation: fadeIn 0.5s ease-out forwards;
          }
        `}</style>

      </section>


      {/* =========================================================
          FAQ
      ========================================================= */}

      <section className="w-full pb-[40px] md:pb-[100px] bg-white">

        <div className="max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px]">

          <div className="flex flex-col lg:flex-row items-start gap-[24px] md:gap-[40px] lg:gap-[80px]">

            {/* LEFT SIDE */}

            <motion.div
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 0.8,
                ease: "easeOut"
              }}
              className="w-full lg:w-5/12 lg:sticky lg:top-[120px]"
            >

              <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] tracking-tight leading-[1.2] lg:max-w-[350px]">

                Frequently Asked Questions About Databricks Consulting in France

              </h2>

            </motion.div>


            {/* RIGHT SIDE */}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{
                once: true,
                amount: 0.1
              }}
              transition={{
                duration: 0.6,
                ease: "easeOut"
              }}
              className="w-full lg:w-7/12 flex flex-col border border-[#E4E8F5] bg-white rounded-sm"
            >

              {databricksFaqData.map((faq, index) => {

                const isActive = faqActiveIndex === index;

                return (

                  <div
                    key={faq.id}
                    className={`flex flex-col border-b ${
                      isActive
                        ? "border-[#ED6800] border-b-[3px] md:border-b-[4px]"
                        : "border-[#E4E8F5]"
                    } last:border-b-0 transition-colors duration-300`}
                  >

                    {/* QUESTION */}

                    <button
                      type="button"
                      onClick={() => toggleFaqAccordion(index)}
                      className="w-full flex justify-between items-center text-left py-[18px] sm:py-[24px] md:py-[32px] px-[16px] sm:px-[24px] md:px-[40px] group cursor-pointer bg-white md:hover:bg-[#F8FAFF] transition-colors duration-300"
                      aria-expanded={isActive}
                    >

                      <h3
                        className={`font-sora max-w-[85%] text-[16px] md:text-[20px] font-[600] pr-[16px] sm:pr-[20px] transition-colors duration-300 ${
                          isActive
                            ? "text-[#01386A]"
                            : "text-[#01386A] md:group-hover:text-[#ED6800]"
                        }`}
                      >

                        {faq.question}

                      </h3>


                      {/* PLUS / MINUS */}

                      <div className="flex-shrink-0 text-[#01386A] transition-colors duration-300 md:group-hover:text-[#ED6800]">

                        {isActive ? (

                          <svg
                            width="18"
                            height="18"
                            className="md:w-[20px] md:h-[20px]"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >

                            <line
                              x1="5"
                              y1="12"
                              x2="19"
                              y2="12"
                            />

                          </svg>

                        ) : (

                          <svg
                            width="18"
                            height="18"
                            className="md:w-[20px] md:h-[20px]"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >

                            <line
                              x1="12"
                              y1="5"
                              x2="12"
                              y2="19"
                            />

                            <line
                              x1="5"
                              y1="12"
                              x2="19"
                              y2="12"
                            />

                          </svg>

                        )}

                      </div>

                    </button>


                    {/* ANSWER */}

                    <AnimatePresence initial={false}>

                      {isActive && (

                        <motion.div
                          initial={{
                            height: 0,
                            opacity: 0
                          }}
                          animate={{
                            height: "auto",
                            opacity: 1
                          }}
                          exit={{
                            height: 0,
                            opacity: 0
                          }}
                          transition={{
                            duration: 0.4,
                            ease: "easeInOut"
                          }}
                          className="overflow-hidden bg-white"
                        >

                          <div className="pb-[20px] sm:pb-[32px] px-[16px] sm:px-[24px] md:px-[40px]">

                            <p className="font-graphik text-[#6C7689] text-[14px] md:text-[16px] font-[500] leading-[1.6]">

                              {faq.answer}

                            </p>

                          </div>

                        </motion.div>

                      )}

                    </AnimatePresence>

                  </div>

                );

              })}

            </motion.div>

          </div>

        </div>

      </section>


      {/* =========================================================
          LEAD FORM
      ========================================================= */}

      <ServiceNowLeadForm />


      {/* =========================================================
          BANNER
      ========================================================= */}

      <Banner
        title="Hire Databricks Consultant in France"
        text="Hire experienced Databricks consultants in France to accelerate implementations, custom development, integrations, workflow automation, and ongoing Databricks projects with flexible engagement options."
      />

    </div>
  );
}

export default HireDatabricksConsultantFrance;